//@ts-check

/**
 * @file Reusable JSDoc type definitions for Product Details / Product Overview.
 */

/**
 * @typedef {Object.<string, string>} LocalizedTextMap
 * Map of locale code to translated text (for example: en, es_MX).
 */

/**
 * @typedef {Object} ProductBaseData
 * @property {string} [externalId] - External product identifier.
 * @property {string} [productId] - Internal product identifier.
 * @property {string} [parentId] - Parent product identifier.
 * @property {string} [gtin] - Global Trade Item Number.
 * @property {string} [scope] - Product scope.
 * @property {string} [country] - Country code.
 * @property {string|LocalizedTextMap|null} [productSize] - Product size or localized size map.
 * @property {string} [productType] - Product type label.
 * @property {string[]} [segments] - Segment identifiers assigned to this product.
 * @property {string} [barCode] - Product barcode.
 * @property {string} [routeTypeId] - Route type identifier.
 * @property {string|null} [productLine] - Product line.
 * @property {string|null} [productGroup] - Product group.
 * @property {string|null} [productContainer] - Product container.
 * @property {string|null} [saleUom] - Sale unit of measure.
 * @property {string|number|null} [allowedInMultiplesOf] - Multiple-of value for ordering.
 * @property {string|number|null} [suggestedSellingPrice] - Suggested selling price.
 * @property {number|null} [sorting] - Sort order value.
 * @property {boolean|string|null} [showOnEcommerce] - E-commerce visibility flag.
 * @property {boolean|string|null} [showOnEngagement] - Engagement visibility flag.
 * @property {string|null} [shortDescription] - Short description.
 * @property {LocalizedTextMap|null} [longDescription] - Localized long description.
 * @property {LocalizedTextMap|null} [productDescription] - Localized product description.
 * @property {string} [name] - Product display/name label.
 * @property {string} [status] - Product status.
 * @property {boolean|string|null} [isReturnable] - Returnable flag.
 * @property {boolean|string|null} [isReturnableFlag] - Legacy returnable flag.
 * @property {boolean|string|null} [isDepositable] - Depositable flag.
 * @property {string|number|null} [skuNumber] - SKU number.
 * @property {string|number|null} [sizeId] - Size id.
 * @property {LocalizedTextMap|null} [sizeByLocale] - Localized size values.
 * @property {string|number|null} [productTypeId] - Product type id.
 * @property {LocalizedTextMap|null} [productTypeByLocale] - Localized product type values.
 * @property {string|null} [description] - Description text.
 * @property {string|null} [businessDescription] - Business description text.
 */

/**
 * @typedef {Object} ProductAdditionalInfo
 * @property {string|number|null} [gtmuId] - GTMU id.
 * @property {string|null} [salesOrg] - Sales organization.
 * @property {string|null} [displayProductSize] - Display product size.
 * @property {string|null} [productCode2] - Secondary product code.
 * @property {string|null} [packageName] - Package name.
 * @property {string|null} [package] - Package value.
 * @property {string|null} [packageCode] - Package code.
 * @property {string|null} [distributionChannel] - Distribution channel code.
 * @property {string|number|null} [displayOrder] - Display order.
 * @property {string|number|null} [minimumOrderQuantity] - Minimum order quantity.
 * @property {string|number|null} [maximumOrderQuantity] - Maximum order quantity.
 * @property {string|number|null} [unitMultiplier] - Unit multiplier.
 * @property {string|number|null} [wishlist] - Wishlist flag/value.
 * @property {string|number|null} [rewardPoints] - Reward points.
 * @property {string|number|null} [returnPriceForBaseUnit] - Return price for base unit.
 * @property {string|number|null} [saleUnitConversionToBase] - Sale to base unit conversion.
 * @property {string|number|null} [allowedInMultiplesOf] - Multiple-of value for ordering.
 * @property {string|number|null} [suggestedSellingPrice] - Suggested selling price.
 * @property {number|string|null} [displaySizeId] - Display size id (positive integer, optional).
 * @property {boolean|string|null} [isNewProduct] - New SKU flag.
 */

/**
 * @typedef {Object} ProductDisplay
 * @property {LocalizedTextMap|null} [displayName] - Localized display name.
 * @property {LocalizedTextMap|null} [displaySize] - Localized display size.
 */

/**
 * @typedef {Object} ProductBrandInfo
 * @property {LocalizedTextMap|string|null} [brand] - Brand value.
 * @property {string|null} [brandId] - Brand id.
 * @property {string|null} [displayBrandId] - Display brand id.
 * @property {string|null} [brandName] - Brand name.
 * @property {string|null} [brandGroupName] - Brand group name.
 * @property {LocalizedTextMap|string|null} [subBrand] - Sub-brand value.
 * @property {string|null} [subBrandId] - Sub-brand id.
 * @property {LocalizedTextMap|null} [displayBrand] - Localized display brand.
 */

/**
 * @typedef {Object} CategoryItem
 * @property {string|null} [categoryId] - Category id.
 * @property {string|null} [categoryName] - Category name.
 * @property {string|null} [categoryType] - Category type.
 */

/**
 * @typedef {Object} SubCategoryItem
 * @property {string|null} [code] - Subcategory code.
 * @property {LocalizedTextMap|null} [values] - Localized subcategory display values.
 * @property {string|null} [url] - Subcategory URL.
 */

/**
 * @typedef {Object} ProductCategory
 * @property {string|null} [erpCategory] - ERP category name.
 * @property {string|null} [erpCategoryId] - ERP category id.
 * @property {string|null} [displayCategoryId] - Display category id.
 * @property {CategoryItem[]} [categories] - Category items assigned to this product.
 * @property {boolean|null} [subCategoryFlag] - Whether the product has subcategories.
 * @property {LocalizedTextMap|null} [displayCategory] - Localized display category.
 * @property {SubCategoryItem[]} [subCategories] - Subcategory items assigned to this product.
 */

/**
 * @typedef {Object} ProductTax
 * @property {string} [taxTypeCode] - Tax type code.
 * @property {string} [taxCountryCode] - Tax country code.
 */

/**
 * @typedef {Object} ProductSaleUom
 * @property {string} [code] - Unit code.
 * @property {string} [name] - Unit display name.
 * @property {number|string|null} [baseUomConversionNumerator] - Conversion numerator.
 * @property {number|string|null} [baseUomConversionDenominator] - Conversion denominator.
 */

/**
 * @typedef {Object} ProductMedia
 * @property {string|null} [mediaUrl] - Media asset URL.
 * @property {number|null} [displayOrder] - Media display order.
 * @property {string|null} [mediaType] - Media type (for example: IMAGE).
 * @property {string|null} [mediaTitle] - Media title.
 */

/**
 * @typedef {Object} ProductDetails
 * @property {ProductBaseData} [baseData] - Product base data section.
 * @property {ProductAdditionalInfo} [additionalInfo] - Product additional info section.
 * @property {ProductDisplay} [display] - Product display section.
 * @property {ProductBrandInfo} [brandInfo] - Product brand section.
 * @property {ProductCategory} [category] - Product category section.
 * @property {ProductTax[]} [taxes] - Product taxes.
 * @property {ProductSaleUom[]} [saleUoms] - Product sale units of measure.
 * @property {ProductMedia[]} [media] - Product media items.
 */

/**
 * @typedef {Object} ProductDetailsApiResponse
 * @property {number} [statusCode] - HTTP-style status code.
 * @property {boolean} [success] - Request success flag.
 * @property {string} [message] - Optional response message.
 * @property {ProductDetails} [data] - Product details payload.
 * @property {string|null} [errorCode] - Optional backend error code.
 * @property {unknown} [domainContext] - Optional domain context payload.
 * @property {string|null} [timestamp] - Optional response timestamp.
 */

/**
 * Runtime map of common product status values for UI reuse.
 * @type {Object.<string, string>}
 */
export const ProductStatus = Object.freeze({
  ACTIVE: 'Active',
  DRAFT: 'Draft',
  INACTIVE: 'Inactive',
})

/**
 * Reusable empty Product Details response shape.
 * @type {ProductDetailsApiResponse}
 */
export const emptyProductDetailsResponse = Object.freeze({
  statusCode: 0,
  success: false,
  message: '',
  data: {
    baseData: {},
    additionalInfo: {},
    display: {},
    brandInfo: {},
    category: {},
    taxes: [],
    saleUoms: [],
    media: [],
  },
})
